import { Flex, Form, Tabs, type TabsProps } from "antd"
import React, {
  forwardRef,
  useCallback,
  useImperativeHandle,
  useState
} from "react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"

import { findFirstErrorTabKey, getTabErrorCounts } from "./tab-field-utils.ts"
import { type TabbedEditFormHandle, type TabDef } from "./tab-types.ts"

interface TabbedEditFormProps {
  tabs: TabDef[]
}

// Module scope: the shouldUpdate render prop below re-runs on every field change.
const renderStickyTabBar: TabsProps["renderTabBar"] = (
  props,
  DefaultTabBar
) => <DefaultTabBar {...props} className={"sticky top-0 z-10 bg-background"} />

export const TabbedEditForm = forwardRef<
  TabbedEditFormHandle,
  TabbedEditFormProps
>(function TabbedEditForm({ tabs }, ref) {
  const form = Form.useFormInstance()
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [submitAttempted, setSubmitAttempted] = useState(false)

  const urlTab = searchParams.get("tab")
  const activeKey = tabs.some((tab) => tab.key === urlTab)
    ? (urlTab as string)
    : tabs[0]?.key

  const onTabChange = useCallback(
    (key: string, replace = false) => {
      setSearchParams(
        (prev) => {
          prev.set("tab", key)
          return prev
        },
        { replace }
      )
    },
    [setSearchParams]
  )

  useImperativeHandle(
    ref,
    () => ({
      goToFirstError: (errorFields) => {
        setSubmitAttempted(true)
        const key = findFirstErrorTabKey(tabs, errorFields)
        if (key && key !== activeKey) onTabChange(key, true)
      }
    }),
    [tabs, activeKey, onTabChange]
  )

  return (
    <Form.Item noStyle shouldUpdate>
      {() => {
        const errorCounts = getTabErrorCounts(tabs, form.getFieldsError())
        const items = tabs.map((tab) => {
          const count = errorCounts.get(tab.key) ?? 0
          return {
            key: tab.key,
            forceRender: true,
            label: (
              <Flex align="center" gap={6}>
                {tab.label}
                {submitAttempted && count > 0 && (
                  <span
                    className="error-count"
                    aria-label={t("tabbedForm.errorBadge", { count }) as string}
                  >
                    {count}
                  </span>
                )}
              </Flex>
            ),
            children: (
              <Flex vertical gap="middle">
                {tab.render()}
              </Flex>
            )
          }
        })
        return (
          <Tabs
            activeKey={activeKey}
            onChange={onTabChange}
            items={items}
            renderTabBar={renderStickyTabBar}
          />
        )
      }}
    </Form.Item>
  )
})

export default TabbedEditForm
